import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { Package, Trash2, Check, X, AlertTriangle } from 'lucide-react';
import { magazzinoAPI } from '../../services/magazzinoApi';
import '../../styles/DistintaPrestazione.css';

/**
 * DistintaPrestazione — quali prodotti consuma una prestazione.
 *
 * PERCHE' NON BASTA UN CAMPO
 * Una seduta di filler consuma la fiala, l'anestetico e il monouso: tre
 * prodotti, ognuno in una quantita' sua, e nessuno dei tre e' una voce di
 * listino. Il legame vive in prestazione_prodotti, e questa e' la
 * schermata che lo scrive.
 *
 * LA QUANTITA'
 * E' quanto se ne consuma per UNA prestazione. Se la visita ne registra
 * due, il magazzino scarica il doppio: il conto lo fa il database, qui si
 * dichiara solo la dose unitaria. Ammette i decimali, perche' mezza fiala
 * e' 0,5 e un millilitro su una siringa da tre e' 0,3333.
 *
 * LA TENDINA
 * Ricercabile e non semplice: i prodotti di un poliambulatorio avviato
 * sono qualche centinaio, e scorrerli a mano e' piu' lento che scriverne
 * tre lettere. Stessa scelta gia' fatta per il campo "A chi" dei compiti.
 *
 * DUE PEZZI, DAL 19 SETTEMBRE 2026
 * `EditorDistinta` e' la tabella e basta: riceve le righe e le restituisce
 * cambiate, non sa cosa sia una prestazione e non salva niente.
 * `DistintaPrestazione` e' la finestra che la carica dal server e la
 * salva, come ha sempre fatto.
 *
 * La separazione serve al modulo «Nuova prestazione», dove la distinta si
 * compone PRIMA che la prestazione esista: li' non c'e' un id da cui
 * leggere ne' a cui scrivere, e le righe viaggiano insieme al resto del
 * modulo in un salvataggio solo. Un interruttore «senza id» dentro questa
 * finestra avrebbe voluto dire un componente che fa due mestieri, e il
 * secondo si sarebbe rotto la prima volta che si tocca il primo.
 */

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

/**
 * Perche' una riga non va bene, o `null` se va bene.
 *
 * Le stesse regole del server (`services/distinta.js`), scritte in
 * italiano invece che in codice di errore. Il controllo che CONTA resta
 * quello del server: questo serve a non far premere un pulsante che
 * rispondera' 400, e a dire quale riga guardare.
 */
export const problemaRiga = (r) => {
    const grezza = String(r.quantita === undefined || r.quantita === null ? '' : r.quantita)
        .trim().replace(',', '.');
    if (grezza === '') return 'manca la quantità';
    const q = Number(grezza);
    if (!Number.isFinite(q)) return 'non è un numero';
    if (q <= 0) return 'deve essere maggiore di zero';
    return null;
};

/** Una riga della distinta e' valida se la quantita' e' un numero > 0. */
export const quantitaNonValide = (righe) => righe.some((r) => problemaRiga(r) !== null);

/**
 * Le righe nella forma che la rotta si aspetta.
 *
 * LA QUANTITA' SI MANDA COM'E' SCRITTA, e non «corretta» qui. C'era un
 * `|| 1` che trasformava in 1 qualunque cosa non fosse un numero: la
 * stessa invenzione che il server faceva su `impostaDistinta`, e che il
 * 19 settembre 2026 e' stata tolta da tutte e due le parti. Se il valore
 * non va, deve arrivare al server e tornare indietro come errore - non
 * diventare una fiala intera al posto di mezza.
 *
 * La virgola resta: la normalizza il server, che e' l'unico posto dove
 * quella regola deve stare scritta.
 */
export const righeDaSalvare = (righe) => righe.map((x) => ({
    prodotto_id: x.prodotto_id,
    quantita: x.quantita,
}));

/**
 * EditorDistinta — la tabella dei prodotti consumati, senza contorno.
 *
 * Controllato: non tiene le righe, le riceve e le rimanda cambiate. Cosi'
 * funziona sia dentro la finestra (che le carica dal server) sia dentro
 * il modulo di una prestazione che ancora non esiste.
 *
 * @param {array}  righe      [{ prodotto_id, nome, codice, unita_misura,
 *                              tracciabilita_rigorosa, giacenza, quantita }]
 * @param {func}   onCambia   (righeNuove) => void
 * @param {array}  prodotti   il catalogo di magazzino
 * @param {bool}   caricamento
 */
export function EditorDistinta({ righe, onCambia, prodotti, caricamento = false }) {
    const [daAggiungere, setDaAggiungere] = useState(null);

    const opzioni = prodotti
        .filter((p) => !righe.some((r) => r.prodotto_id === p.prodotto_id))
        .map((p) => ({
            value: p.prodotto_id,
            label: `${p.nome}${p.codice ? ` (${p.codice})` : ''}`,
            prodotto: p,
        }));

    const aggiungi = (scelta) => {
        if (!scelta) return;
        const p = scelta.prodotto;
        onCambia([...righe, {
            prodotto_id: p.prodotto_id,
            nome: p.nome,
            codice: p.codice,
            unita_misura: p.unita_misura,
            tracciabilita_rigorosa: p.tracciabilita_rigorosa,
            giacenza: p.giacenza,
            quantita: '1',
        }]);
        setDaAggiungere(null);
    };

    const cambiaQuantita = (id, valore) =>
        onCambia(righe.map((r) => (r.prodotto_id === id ? { ...r, quantita: valore } : r)));

    const togli = (id) => onCambia(righe.filter((r) => r.prodotto_id !== id));

    if (caricamento) return <p className="dist-attesa">Caricamento…</p>;

    return (
        <>
            {righe.length === 0 ? (
                <p className="dist-vuoto">
                    Nessun prodotto collegato: eseguire questa prestazione non
                    muove il magazzino.
                </p>
            ) : (
                <table className="dist-tabella">
                    <thead>
                        <tr>
                            <th>Prodotto</th>
                            <th className="num">Per una prestazione</th>
                            <th className="num">In giacenza</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {righe.map((r) => (
                            <tr key={r.prodotto_id}>
                                <td>
                                    {r.nome}
                                    <span className="dist-sub">
                                        {r.codice}
                                        {r.tracciabilita_rigorosa && ' · lotto da etichetta'}
                                    </span>
                                    {/* L'avviso per esteso, e non solo la sigla
                                        accanto al codice: chi compila la distinta
                                        deve sapere che su questi prodotti la
                                        quantita' e' un valore di partenza, non il
                                        consumo reale - quello lo dice il lotto
                                        letto dall'etichetta al momento dell'uso. */}
                                    {r.tracciabilita_rigorosa && (
                                        <span className="dist-tracciato">
                                            <AlertTriangle size={12} aria-hidden="true" />
                                            lotto da etichetta — lo scarico non è automatico
                                        </span>
                                    )}
                                </td>
                                <td className="num">
                                    <input type="text" inputMode="decimal"
                                           className={problemaRiga(r) ? 'dist-qta dist-qta--errata' : 'dist-qta'}
                                           value={r.quantita}
                                           aria-invalid={problemaRiga(r) ? 'true' : undefined}
                                           onChange={(e) => cambiaQuantita(r.prodotto_id, e.target.value)} />
                                    <span className="dist-um">{r.unita_misura}</span>
                                    {/* Il perche' accanto al campo, non in fondo:
                                        su cinque righe un messaggio generale non
                                        dice quale correggere. */}
                                    {problemaRiga(r) && (
                                        <span className="dist-qta-errore">{problemaRiga(r)}</span>
                                    )}
                                </td>
                                <td className="num dist-giacenza">
                                    {r.giacenza != null ? parseFloat(r.giacenza) : '—'}
                                </td>
                                <td className="num">
                                    <button type="button" className="dist-icona"
                                            title="Togli dalla distinta"
                                            onClick={() => togli(r.prodotto_id)}>
                                        <Trash2 size={15} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <label className="dist-aggiungi">
                <span>Aggiungi un prodotto</span>
                <Select
                    options={opzioni}
                    value={daAggiungere}
                    onChange={aggiungi}
                    placeholder="Scrivi per cercare…"
                    noOptionsMessage={() => (prodotti.length
                        ? 'Nessun altro prodotto'
                        : 'Nessun prodotto a magazzino: si creano dalla schermata Magazzino')}
                    isClearable
                    classNamePrefix="dist-select"
                />
            </label>

            <p className="dist-nota">
                La quantità è quella di <b>una</b> prestazione. Se la visita ne
                registra due, il magazzino scarica il doppio da solo. Sui
                prodotti a lotto obbligatorio lo scarico non è automatico: la
                riga resta in «Da scaricare» finché la segreteria non legge il
                lotto dall'etichetta.
            </p>
        </>
    );
}

const DistintaPrestazione = ({ prestazione, onChiudi, onSalvata }) => {
    const [righe, setRighe] = useState([]);
    const [prodotti, setProdotti] = useState([]);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const [d, p] = await Promise.all([
                magazzinoAPI.distinta(prestazione.id),
                magazzinoAPI.prodotti({}),
            ]);
            setRighe((d.data.data.distinta || []).map((x) => ({
                prodotto_id: x.prodotto_id,
                nome: x.nome,
                codice: x.codice,
                unita_misura: x.unita_misura,
                tracciabilita_rigorosa: x.tracciabilita_rigorosa,
                giacenza: x.giacenza,
                // I quattro decimali del database sono precisione di calcolo,
                // non qualcosa da mostrare: 1,0000 si legge peggio di 1.
                quantita: String(parseFloat(x.quantita)),
            })));
            setProdotti(p.data.data.prodotti || []);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare la distinta'));
        } finally {
            setCaricamento(false);
        }
    }, [prestazione.id]);

    useEffect(() => { carica(); }, [carica]);

    const salva = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            const r = await magazzinoAPI.impostaDistinta(prestazione.id, righeDaSalvare(righe));
            if (onSalvata) onSalvata(r.data.message, righe.length);
            onChiudi();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio della distinta'));
        } finally {
            setInCorso(false);
        }
    };

    return (
        <div className="dist-sfondo" onClick={onChiudi}>
            <div className="dist-modale" onClick={(e) => e.stopPropagation()}>
                <div className="dist-testa">
                    <h2><Package size={18} /> Prodotti consumati</h2>
                    <button onClick={onChiudi} aria-label="Chiudi"><X size={18} /></button>
                </div>

                <p className="dist-prestazione">{prestazione.nome}</p>

                {errore && (
                    <div className="dist-errore">
                        <AlertTriangle size={15} /> {errore}
                    </div>
                )}

                <EditorDistinta
                    righe={righe}
                    onCambia={setRighe}
                    prodotti={prodotti}
                    caricamento={caricamento}
                />

                <div className="dist-fondo">
                    <button className="dist-btn" onClick={onChiudi}>Annulla</button>
                    <button className="dist-btn dist-btn--principale"
                            onClick={salva}
                            disabled={inCorso || caricamento || quantitaNonValide(righe)}>
                        <Check size={15} /> Salva
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DistintaPrestazione;
